import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import axios from 'axios';

import { MarketplaceSelector } from '@shipstation/connections-ui';
import { DirectMarketplaceConnection } from '@shipstation/connections-ui/direct';
import { noop } from 'ui-components/default-props/noop';

import Global from '../store/global-selectors';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useCommonCuiProps } from './helpers/use-common-cui-props';
import { useCuiApiConfig } from './helpers/use-cui-api-config';
import { useCuiContextProps } from './helpers/use-cui-context-props';
import { useEnableRemoveIframeMarketplace } from '../feature-flags/hooks/use-enable-remove-iframe-marketplace';
import { LogService } from '../services/log-service';
import { MarketplaceIds } from '../enums/marketplace-ids';
import { useIsSellerFeatureEnabled } from '../hooks/use-is-seller-feature-enabled';
import { Features } from '../constants/features';
import { useCanAddExternalStore } from '../feature-gates/use-external-stores-access-feature-gate';
import { useOrderImportCustomStoreFeatureGate } from '../feature-gates/use-order-import-custom-store-feature-gate';

export const CuiMarketplaceSelector = ({ onMarketplaceSelected = noop, onError = noop }) => {
  const isDirectMode = useEnableRemoveIframeMarketplace();
  const commonCuiProps = useCommonCuiProps();
  const cuiApiConfig = useCuiApiConfig();
  const cuiContextProps = useCuiContextProps();
  const [loading, setLoading] = useAsyncSafeState(true);
  const [marketplaceList, setMarketplaceList] = useAsyncSafeState([]);
  const sellerStores = useSelector(Global.selectSellerActiveStores);
  const shouldHideOdbc = !useIsSellerFeatureEnabled(Features.ODBC);
  const canAddExternalStore = useCanAddExternalStore();
  const { isFeatureEnabled: hasCustomStoreAccess } = useOrderImportCustomStoreFeatureGate();

  const directContextProps = useMemo(
    () => ({ ...cuiContextProps, marketplaceList, onMarketplaceSelected, onError }),
    [cuiContextProps, marketplaceList, onMarketplaceSelected, onError],
  );

  useEffect(() => {
    axios
      .get('/marketplace/availableMarketplaces')
      .then((response) => {
        let marketplaces = response.data;

        if (shouldHideOdbc) {
          marketplaces = marketplaces.filter((x) => x.marketplaceId !== MarketplaceIds.ODBC);
        }

        marketplaces = marketplaces.map((marketplace) => {
          const marketplaceId = Number.parseInt(marketplace.marketplaceId, 10);
          const alreadyConnected = sellerStores.some((p) => p.marketplaceId === marketplace.marketplaceId);
          const isCustom = marketplaceId === MarketplaceIds.CustomStore;
          const isExternal = marketplaceId !== MarketplaceIds.ShipStation && marketplaceId !== MarketplaceIds.CustomStore;
          const disabled = (isCustom && !hasCustomStoreAccess) || (isExternal && !canAddExternalStore);

          return {
            ...marketplace,
            marketplaceId,
            alreadyConnected,
            disabled,
          };
        });

        // sort by non disabled marketplaces first, otherwise keep the original order
        const sortedMarketplaces = marketplaces.sort((a, b) => {
          if (a.disabled && !b.disabled) return 1;
          if (!a.disabled && b.disabled) return -1;
          return 0;
        });

        setMarketplaceList(sortedMarketplaces);
      })
      .catch((error) => {
        LogService.logInfo(error.code, error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, setMarketplaceList, sellerStores, onError, shouldHideOdbc, canAddExternalStore, hasCustomStoreAccess]);

  if (loading) {
    return <LoadingIndicator />;
  }

  if (isDirectMode) {
    return <DirectMarketplaceConnection apiConfig={cuiApiConfig} contextProps={directContextProps} />;
  }

  return (
    <MarketplaceSelector
      {...commonCuiProps}
      marketplaceList={marketplaceList}
      onError={onError}
      onMarketplaceSelected={onMarketplaceSelected}
    />
  );
};

CuiMarketplaceSelector.propTypes = {
  onMarketplaceSelected: PropTypes.func,
  onError: PropTypes.func,
};
